import { OWObjectList } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { ConditionParams } from "../../../../../Global/Base/DataSources/Interfaces";
import { Event, StatefulEvent } from "../../../../../Global/Base/Events";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { IDataGrid } from "../../../../UI.Controls/IDataGrid";
import { IFrameProfileInfo } from "../../IFrameColumnInfo";
import { FrameContext } from "../../UIManagerInterfaces";
import { IFilterManager } from "../Filters/IFilterManager";
import { FrameSelChanged } from "../Frames";
import { IForm } from "../IForm";
import { IFrame, IFrameWithRights } from "../IFrame";
import { ValidationManager } from "../ValidationManager";

/**
 * Frame podporující filtrování
 */
export interface IFrameWithFiltering extends IFrame {
    /**
     * FilterManager framu
     */
    readonly filterManager: IFilterManager | null;

    /**
     * Identifikátor filtru, který se má použít po inicializaci framu
     * nebo po načtení dat při použití Query módu
     */
    readonly defaultFilterId: string | null;
    /**
     * Identifikátor filtru, který se má použít po inicializaci framu
     * nebo po načtení dat při použití Query módu
     * Lze nastavit jen pokud frame ještě není zinicializovaný.
     * @param filterId Identifikátor filtru
     * @returns true, pokud se podařilo identifikátor filtru nastavit. Jinak vrací false.
     */
    setDefaultFilterId(filterId: string | null): boolean;
    /**
     * načtení profilu formuláře
     * @param filterId Identifikátor filtru
     * @param queryMode Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data
     */
    loadProfile(filterId: string, queryMode: boolean): void;
    /**
     * Přiznak, který určuje zda jde o první filtrování
     */
    isInitExecuteFilter: boolean;
}

export class IFrameWithFiltering {
    private static _properties: (keyof IFrameWithFiltering)[] = ["filterManager", "defaultFilterId"];
    private static _methods: (keyof IFrameWithFiltering)[] = ["setDefaultFilterId"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IFrame;
    }
}

/**
 * Rozhraní pro frame, který může být masterem jiného framu
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IMasterableFrame extends IFrameWithRights {
    /**
     * Pomocná proměnná pro určení mastera
     */
    readonly canBeMasterFrame: boolean;

    /**
     * Událost vyvolaná při změně vybraného záznamu
     */
    readonly selectionChanged: Event<IMasterableFrame, FrameSelChanged>;

    /**
     * Vrací promis s probíhajícím vyhodnocováním práv, na které lze čekat
     */
    readonly rightsEvaluationTask?: Promise<void>;

    /**
     * Pokud je nastaveno na true, tak se viditelnost nedá změnit a frame je za všech podmínek skrytý.
     * Slouží například u konstrukcí formulářů s hlavním skrytým framem (má vlastnost Visible=false),
     * ke kterému je zobrazen podřízený frame (typicky DetaiLFrame).
     */
    permanentInvisible?: boolean;

    /**
     * Událost volaná při uložení aktuálního objektu
     */
    readonly actualObjectSaved: Event<IMasterableFrame, OWObject>;

    /**
     * Zda mají být na framu zobrazeny i záznamy patřící podřízeným záznamům vybraného master záznamu
     */
    showSubRecords?: boolean;

    /**
     * Příznak, který určuje zdali by měly podřízené framy donačíst záznamy i když se jedná o nově vytvořený záznam z MasterFramu.
     * Pokud má nastavenou vlastnost na true, tak podřízené framy donačtou záznamy.
     * */
    loadDataForNewObject?: boolean;

    /**
     * Určuje, jestli je vlastníkem menu a má jej disposnout.
     */
    isMenuOwner?: boolean;
}

export class IMasterableFrame {
    private static _properties = ["canBeMasterFrame", "selectionChanged"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IFrame;
    }
}

export interface IMasterableBase {
    /**
     * Obsahuje master současného framu
     */
    masterFrame: IMasterableFrame;
    /**
     * Nadřízeny formulář
     */
    ownerForm: IForm;
    /**
     * Zdali se má použít kontext z MasterFramu
     */
    useMasterContext?: boolean;
    /**
     * Zdali je kontext vytvořený z XGAction - týká se např. parametřáku
     */
    isContextCreatedByAction: boolean;
    /**
     * Identifikační číslo transakčního kontextu
     */
    transactionContextId: string | undefined;
    /**
     * Objekt s metodou pro získáni transakčního kontextu a rolí
     */
    frameContext: FrameContext;

    /**
    * Název domenové třídy zobrazované ve framu
    */
    objectName: string;
    /**
     * Informace o tom jestli třída se kterou komponenta pracuje podporuje editační kontexty
     * @returns Příznak, jestli komponenta pracuje nad třídou která umožňuje práci v editačním kontextu nebo `null` pokud to nelze určit (např. u CalendarFrame, který pracuje nad různými třídami)
     */
    readonly supportsEditContext?: Promise<boolean | null>;
    /**
     * Ukazatel zda frame vytvořil editační kontext
     */
    readonly isEditContextInitiator: boolean;
    /**
     * Ukazatel jestli se používá předaný editační kontext
     */
    readonly hasHandedContext: boolean;
    /**
     * Událost, která se vyvolá při vytvoření kontextu
     */
    readonly contextCreated: Event<IMasterableBase, boolean>;
    /**
     * Událost, která se vyvolá po uložení kontextu
     */
    readonly contextSaved: Event<IMasterableBase, boolean>;
    /**
     * Událost, která se vyvolá po zrušení kontextu
     */
    readonly contextRollbacked: Event<IMasterableBase, boolean>;
    /**
     * Vytvoří editační kontext framu
     */
    createContext(): Promise<void>;

    /**
     * Zruší editační kontext framu
     */
    rollbackContext(): Promise<boolean | void>;

    /**
     * Uloží editační kontext framu
     */
    saveContext(): Promise<boolean | void>;
}

export class IMasterableBase {
    private static _properties = ["masterFrame", "ownerForm", "transactionContextId", "frameContext", "isEditContextInitiator", "hasHandedContext", "contextCreated", "contextSaved", "contextRollbacked", "supportsEditContext", "objectName", "isContextCreatedByAction"];
    private static _methods = ["createContext", "rollbackContext", "saveContext"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro komponentu, která zobrazuje více záznamů
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IMultiRecordsComponent /*extends IMasterableFrame*/
{
    /**
     * Určuje, zda je vybráno více záznamů
     */
    readonly isMultiSelecting: boolean;

    /**
     * Určuje, jestli frame umožňuje vstoupit do multi editace.
     */
    readonly canEnterMultiEdit: boolean;

    /*
     * Příznak, jestli byla provedena alespoň jedna hromadná změna
     */
    readonly isMultiEditing: boolean;
    /**
     * Určuje mód výběru, jestli je mód pro více záznamu či nikoli
     */
    isMultiSelectingMode: boolean;

    /**
     * Událost při změně výběru jednoho(nebo žádného) záznamu na více záznamů a zpět.
     */
    readonly selectionModeChanged: Event<IMasterableFrame, boolean>;
}

export class IMultiRecordsComponent {
    private static _properties = ["isMultiSelectingMode", "selectionModeChanged", "isMultiSelecting", "canEnterMultiEdit", "isMultiEditing"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));//&& instance instanceof IMasterableFrame;
    }
}

/**
 * Rozhraní pro komponentu, která umožňuje výběr více záznamů
 */
export interface IMultiselectComponent {
    /**
     * Značí, zdali byl aktivován výběr všech záznamu. Označeny jsou všechny záznamy na daném objektu s danou podmínkou
     * (podmínka z definice, fulltextSearch podmínka, ...) včetně nenačtených záznamů. Uživatel může jednotlivé záznamy odoznačit.
     */
    isSelectedAll: boolean;
    /** Získá pole identifikátorů označených (checked) záznamů. */
    selectedIds: string[];
    /** Získá pole identifikátorů odoznačených (!checked) záznamů. */
    unselectedIds: string[];
    /** Získá podmínku načtení záznamů pro DataSource instanci získanou pomocí `dataSourceKey`. */
    loadingCondition: string;
    /** Získá **conditionParams** pro DataSource instanci získanou pomocí `dataSourceKey`. */
    loadingConditionParams: ConditionParams;
    /** Vytvoří OWObjectList reprezentující vybrané záznamy. */
    createSelectionObjectList(): OWObjectList;
}

export class IMultiselectComponent {
    private static _properties: (keyof IMultiselectComponent)[] = [
        "isSelectedAll",
        "selectedIds",
        "unselectedIds",
        "loadingCondition",
        "loadingConditionParams",
        "createSelectionObjectList",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && this._properties.every((value) => Reflect.has(instance, value));
    }
}

/**
 * Rozhraní pro frame, který může být masterem jiného framu
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IValidatableMasterableFrame extends IMasterableFrame {
    /**
     * Validační manažer
     */
    readonly validationManager: ValidationManager;
}

export class IValidatableMasterableFrame {
    private static _properties = ["validationManager"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * @generateInstanceClass
 */
export interface IWorkflowMasterableFrame extends IMasterableFrame {
    reloadWFItems(): void;
}

export class IWorkflowMasterableFrame {
    //private static _properties = ["canBeMasterFrame", "selectionChanged"];
    private static _methods = ["reloadWFItems"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && //!this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * @generateInstanceClass
 */
export interface IEditStyleMasterableFrame extends IMasterableFrame {
    editStyle: EditStyleType;
    isEditable: boolean;
}

export class IEditStyleMasterableFrame {
    private static _properties = ["editStyle", "isEditable"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * @generateInstanceClass
 */
export interface ICompactViewMasterableFrame extends IMasterableFrame {
    checkIfIsCompact(): void;
}

export class ICompactViewMasterableFrame {
    //private static _properties = ["currentDataGrid"];
    private static _methods = ["checkIfIsCompact"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && //!this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function")
            && instance instanceof IMasterableFrame;
    }
}

/**
 * Rozhraní pro frame, který má DataGrid
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IMasterableFrameWithDataGrid extends IMasterableFrame {
    /**
     * Událost o proběhlé inicializaci dataGridu
     */
    readonly dataGridInitialized: StatefulEvent<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>;

    /**
     * Getter pro čtení aktuálního dataGridu, pokud jich má frame víc
     */
    readonly currentDataGrid: IDataGrid<IDataRowElement>;

    /**
     * Název (vazebního) atributu, v němž se nachází záznam, vůči němuž se vyhledá
     * formulář pro otevření, pokud je nastaven DetailForm na IMPLICIT.
     */
    readonly formObjectName: string;
}

export class IMasterableFrameWithDataGrid {
    private static _properties = ["currentDataGrid", "dataGridInitialized", "formObjectName"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * @generateInstanceClass
 */
export interface IFrameWithKeyboardNavigation extends IMasterableFrame {
    /**
     * Událost o změně výběru pomocí klávesnice
     */
    readonly keyboardSelectionChangeInitiated: Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>;
}

export class IFrameWithKeyboardNavigation {
    private static _properties = ["keyboardSelectionChangeInitiated"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * Rozhraní pro frame umožňující práci s profily
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IFrameWithProfile {
    /**
     * Data profilu frame
     */
    profileInfo: IFrameProfileInfo;
    /**
     * Aktuálně vybrané atributy podle kterých se grupuje
     */
    currentAttributesToGroup?: string[];

    /**
     * Událost při změně profilu na jiný
     */
    readonly frameProfileChanged: Event<IFrameWithProfile, void>;
    /**
     * Událost při změně aktuálního profilu
     */
    readonly selectedProfileChanged: Event<IFrameWithProfile, IFrameProfileInfo>;
    /**
     * Smaže profil a nastaví ho do výchozího nastavení
     */
    clearProfileInfo(): void;
}

export class IFrameWithProfile {
    private static _properties: string[] = ["profileInfo", "frameProfileChanged", "selectedProfileChanged"];
    private static _methods = ["clearProfileInfo"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * @generateInstanceClass
 */
export interface ISpecificallyLoadableFrame {
    /**
     * Označí nebo načte a označí záznam s předaným dataKey
     */
    loadAndSelectRecord(dataKey: string, deselectCurrent?: boolean): Promise<void>;
}

export class ISpecificallyLoadableFrame {
    private static _properties: string[] = [];
    private static _methods = ["loadAndSelectRecord"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/** Informace pro fulltextové vyhledávání */
export interface IFulltextSearchCondition {
    /** Podmínka pro načítání dat získaná z fulltextového vyhledávání. */
    readonly condition?: string;
    /** Vyhledávaný text ve fulltextovém vyhledávání */
    readonly searchString?: string;
}

/**
 * Rozhraní pro frame, který podporuje fulltextové vyhledávání
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IFulltextSearchableFrame {
    /**
     * Atributy, které se fulltextově prohledávají
     */
    fulltextAttributes: string[];

    /**
     * Podmínka pro fulltextové vyhledávání
     */
    fulltextCondition: IFulltextSearchCondition;

    createReaderCondition(): Promise<string>;

    updateConditionAndRead(): Promise<void>;
}

export class IFultextSearchableFrame {
    private static _properties = ["fulltextAttributes", "fulltextCondition"];
    private static _methods = ["createReaderCondition", "updateConditionAndRead"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro frame udávající zda se bude kontrolovat na změny
 * @generateInstanceClass
 */
export interface IMFNoChangeCheck extends IMasterableFrame {
    readonly imfNoChangeCheck: boolean;
}

export class IMFNoChangeCheck {
    private static _properties = ["imfNoChangeCheck"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IMasterableFrame;
    }
}

/**
 * Interface pro rozpoznání ObjectListEdit
 * Vytvořeno kvůli #27185 protože formUI pro zobrazení kontextového menu "..." porovnává id actualObjectu framu s ID vybíraného záznamu, což u OLE nebude nikdy stejné.
 * TODO: vyřešit lépe. Ideálně rozhraní zrušit.
 */
export interface IObjectListEdit {
    isOLE: boolean;
}

export class IObjectListEdit {
    private static _properties = ["isOLE"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

export enum EditStyleType { esMulti, esSingle }

export interface IFrameWithChildFrames {
    readonly childFrames: IFrame[];
}

export class IFrameWithChildFrames {
    private static _properties = ["childFrames"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.some(value => Reflect.has(instance, value));
    }
}

export interface IFrameWithUniqueIAttribute {
    /** Slouží k identifikaci framu podle I atributu, který je generován XGENem */
    uniqueIAttrIdentifier: string;
}

export class IFrameWithUniqueIAttribute {
    private static _properties: string[] = ["uniqueIAttrIdentifier"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}
